import { useEffect } from "react";

const SITE = "https://siyaara.com";

/** Inject/replace a JSON-LD script tag. Cleans up on unmount. */
export function JsonLd({ id, data }) {
  useEffect(() => {
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.text = JSON.stringify(data);
    document.head.appendChild(s);
    return () => document.getElementById(id)?.remove();
  }, [id, JSON.stringify(data)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Organization + WebSite (with SearchAction) — mounted site-wide in StoreLayout. */
export function SiteJsonLd() {
  return (
    <JsonLd id="site-jsonld" data={{
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${SITE}/#org`,
          name: "Siyaara",
          url: SITE,
          description: "Live-priced 999 gold & silver coins and fine jewellery, BIS-hallmarked, from Mumbai.",
          address: { "@type": "PostalAddress", addressLocality: "Mumbai", addressRegion: "Maharashtra", postalCode: "400002", addressCountry: "IN" },
        },
        {
          "@type": "WebSite",
          "@id": `${SITE}/#website`,
          url: SITE,
          name: "Siyaara",
          publisher: { "@id": `${SITE}/#org` },
          potentialAction: {
            "@type": "SearchAction",
            target: { "@type": "EntryPoint", urlTemplate: `${SITE}/search?q={search_term_string}` },
            "query-input": "required name=search_term_string",
          },
        },
      ],
    }} />
  );
}

/** BreadcrumbList structured data matching the visible breadcrumbs. */
export function BreadcrumbJsonLd({ items }) {
  return (
    <JsonLd id={`bc-${items.map((i) => i.name).join("-").length}-${items.length}`} data={{
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem", position: i + 1, name: it.name, item: `${SITE}${it.url}`,
      })),
    }} />
  );
}

/** Set Open Graph + Twitter card tags (per-page image override supported). */
export function useOgTags({ title, description, image, url, type = "website" }) {
  useEffect(() => {
    const set = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    if (title) {
      set("property", "og:title", title);
      set("name", "twitter:title", title);
    }
    if (description) {
      set("property", "og:description", description);
      set("name", "twitter:description", description);
    }
    set("property", "og:type", type);
    set("name", "twitter:card", image ? "summary_large_image" : "summary");
    set("property", "og:site_name", "Siyaara");
    if (url) set("property", "og:url", url);
    if (image) {
      set("property", "og:image", image);
      set("name", "twitter:image", image);
    }
  }, [title, description, image, url, type]);
}

export { SITE };
